import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  VStack,
  HStack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  IconButton,
  Card,
  CardBody,
  Divider,
  Heading,
  useClipboard,
} from "@chakra-ui/react";
import { FiCreditCard, FiCheckCircle, FiCopy, FiClock } from "react-icons/fi";
import { useSetupDirectDebit, useNameEnquiry, useGetMandateStatus } from "../../services/payrollService";

// Nigerian banks list (matching EmployeeBankDetails for consistency)
const NIGERIAN_BANKS = [
  { code: "044", name: "Access Bank" },
  { code: "058", name: "GTBank" },
  { code: "033", name: "UBA" },
  { code: "214", name: "First Bank" },
  { code: "011", name: "First Bank" },
  { code: "050", name: "Ecobank" },
  { code: "070", name: "Fidelity Bank" },
  { code: "221", name: "Stanbic IBTC" },
  { code: "068", name: "Standard Chartered" },
  { code: "232", name: "Sterling Bank" },
  { code: "032", name: "Union Bank" },
  { code: "215", name: "Unity Bank" },
  { code: "035", name: "Wema Bank" },
  { code: "057", name: "Zenith Bank" },
  { code: "076", name: "Polaris Bank" },
  { code: "082", name: "Keystone Bank" },
  { code: "090", name: "VFD Microfinance Bank" },
  { code: "100", name: "Suntrust Bank" },
  { code: "101", name: "Providus Bank" },
  { code: "102", name: "Titan Trust Bank" },
  { code: "103", name: "Globus Bank" },
  { code: "104", name: "Parallex Bank" },
  { code: "105", name: "Premium Trust Bank" },
  { code: "106", name: "Optimus Bank" },
  { code: "107", name: "Moniepoint Microfinance Bank" },
  { code: "108", name: "Opay" },
  { code: "109", name: "Kuda Bank" },
  { code: "110", name: "VBank" },
  { code: "111", name: "Mint Microfinance Bank" },
  { code: "112", name: "M42 Microfinance Bank" },
  { code: "113", name: "Carbon" },
  { code: "114", name: "Fairmoney Microfinance Bank" },
  { code: "115", name: "Sparkle Microfinance Bank" },
  { code: "116", name: "Kredi Money Microfinance Bank" },
  { code: "117", name: "9 Payment Service Bank" },
  { code: "118", name: "Airtel Smartcash PSB" },
  { code: "119", name: "MTN Momo PSB" },
  { code: "120", name: "PalmPay" }
];

const DirectDebitSetup = ({ isOpen, onClose, onSuccess }) => {
  const toast = useToast();
  const [step, setStep] = useState("form"); // "form" | "transfer-instructions" | "polling"
  const [formData, setFormData] = useState({
    account_number: "",
    bank_code: "",
    bank_name: "",
    account_name: "",
    email: "",
    phone_number: "",
    address: "",
  });
  const [errors, setErrors] = useState({});
  const [isVerifyingAccount, setIsVerifyingAccount] = useState(false);
  const [accountVerificationError, setAccountVerificationError] = useState(null);
  
  // Mandate consent data from setup response
  const [mandateConsent, setMandateConsent] = useState(null);
  const [instructions, setInstructions] = useState("");
  
  // Countdown timer
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes in seconds
  const countdownIntervalRef = useRef(null);
  
  // Polling state
  const pollingIntervalRef = useRef(null);
  const pollingStartTimeRef = useRef(null);
  const autoPollStartTimeRef = useRef(null);
  const [pollingStatus, setPollingStatus] = useState("pending"); // "pending" | "approved" | "active"
  const [isPolling, setIsPolling] = useState(false);
  const [hasStartedPolling, setHasStartedPolling] = useState(false);
  const [timeoutWarningShown, setTimeoutWarningShown] = useState(false);

  const nameEnquiryMutation = useNameEnquiry();
  const { refetch: refetchMandateStatus } = useGetMandateStatus();
  
  // Copy to clipboard
  const { hasCopied: accountNumberCopied, onCopy: onCopyAccountNumber } = useClipboard(
    mandateConsent?.account_number || ""
  );

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setStep("form");
      setFormData({
        account_number: "",
        bank_code: "",
        bank_name: "",
        account_name: "",
        email: "",
        phone_number: "",
        address: "",
      });
      setErrors({});
      setAccountVerificationError(null);
      setIsVerifyingAccount(false);
      setMandateConsent(null);
      setInstructions("");
      setTimeRemaining(600);
      setPollingStatus("pending");
      setIsPolling(false);
      setHasStartedPolling(false);
      setTimeoutWarningShown(false);
      
      // Clear intervals
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      pollingStartTimeRef.current = null;
      autoPollStartTimeRef.current = null;
    }
  }, [isOpen]);

  const { setupDirectDebitPayload, setupDirectDebitIsLoading, setupDirectDebitError } = useSetupDirectDebit((data) => {
    console.log("Setup Direct Debit Success Response:", data);
    
    // Handle new e-mandate response structure
    const mandateData = data?.mandate || data;
    const consent = mandateData?.mandate_consent;
    const reference = mandateData?.reference || mandateData?.mandate_code;
    const mandateCodeValue = mandateData?.mandate_code;
    const instructionsText = mandateData?.instructions || "";
    
    if (consent && reference) {
      // Store mandate consent data
      setMandateConsent(consent);
      setInstructions(instructionsText);
      
      // Store reference for status checking
      sessionStorage.setItem('pending_mandate_reference', reference);
      if (mandateCodeValue) {
        sessionStorage.setItem('pending_mandate_code', mandateCodeValue);
      }
      
      // Move to transfer instructions step
      setStep("transfer-instructions");
      
      // Start countdown timer
      setTimeRemaining(600);
      startCountdownTimer();
      
      // Set flag for auto-poll after 30 seconds
      autoPollStartTimeRef.current = Date.now();
      
    } else {
      toast({
        title: "Setup Incomplete",
        description: data?.message || "Direct Debit setup completed but transfer details not found. Please try again or contact support.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  });


  // Auto-trigger name enquiry when both bank_code and account_number are available
  useEffect(() => {
    if (
      formData.bank_code &&
      formData.account_number &&
      formData.account_number.length >= 10
    ) {
      // Debounce the API call
      const timeoutId = setTimeout(() => {
        triggerNameEnquiry(formData.bank_code, formData.account_number);
      }, 1000); // Wait 1 second after user stops typing
      
      return () => clearTimeout(timeoutId);
    } else {
      // Clear account name if conditions aren't met
      if (formData.account_name) {
        setFormData(prev => ({ ...prev, account_name: "" }));
      }
      setAccountVerificationError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.bank_code, formData.account_number]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    pollingStartTimeRef.current = null;
  }, []);

  // Poll mandate status
  const pollMandateStatus = useCallback(async () => {
    const POLLING_INTERVAL = 4000; // 4 seconds
    const MAX_POLLING_TIME = 180000; // 3 minutes (180 seconds)
    
    try {
      const { data: updatedStatus } = await refetchMandateStatus();
      const mandate = updatedStatus?.mandate;
      const mandateStatus = mandate?.status?.toLowerCase();
      
      // Check status progression: PENDING → APPROVED → ACTIVE
      if (mandateStatus === "active") {
        // Mandate is active - success!
        setPollingStatus("active");
        stopPolling();
        
        // Clear stored references
        sessionStorage.removeItem('pending_mandate_reference');
        sessionStorage.removeItem('pending_mandate_code');
        
        // Clear countdown timer
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        
        toast({
          title: "✅ Direct Debit Authorized!",
          description: "Your Direct Debit mandate is now active and ready to use.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        
        // Call onSuccess callback and close modal after short delay
        setTimeout(() => {
          if (onSuccess) onSuccess();
          onClose();
        }, 1500);
        
        return;
      } else if (mandateStatus === "approved" || mandate?.status === "APPROVED") {
        // Transfer received, token is approved but not yet active
        setPollingStatus("approved");
      } else {
        // Still pending
        setPollingStatus("pending");
      }
      
      // Check if we've exceeded max polling time
      const elapsed = Date.now() - (pollingStartTimeRef.current || Date.now());
      if (elapsed >= MAX_POLLING_TIME) {
        stopPolling();
        toast({
          title: "Verification Timeout",
          description: "Authorization is taking longer than expected. Please check back in a few moments or contact support if the transfer was completed.",
          status: "info",
          duration: 8000,
          isClosable: true,
        });
        return;
      }
      
      // Continue polling
      pollingIntervalRef.current = setTimeout(pollMandateStatus, POLLING_INTERVAL);
    } catch (error) {
      console.error("Error polling mandate status:", error);
      const elapsed = Date.now() - (pollingStartTimeRef.current || Date.now());
      
      if (elapsed >= 180000) { // 3 minutes
        stopPolling();
        toast({
          title: "Status Check Failed",
          description: "Unable to verify mandate status. Please check your mandate status manually or contact support.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        // Continue polling on error
        pollingIntervalRef.current = setTimeout(pollMandateStatus, POLLING_INTERVAL);
      }
    }
  }, [refetchMandateStatus, stopPolling, onSuccess, onClose, toast]);

  // Start polling mandate status
  const handleStartPolling = useCallback(() => {
    if (isPolling || hasStartedPolling) {
      return;
    }
    
    setIsPolling(true);
    setHasStartedPolling(true);
    setStep("polling");
    
    if (!pollingStartTimeRef.current) {
      pollingStartTimeRef.current = Date.now();
    }
    
    // Start polling immediately
    pollMandateStatus();
  }, [isPolling, hasStartedPolling, pollMandateStatus]);

  // Auto-start polling after 30 seconds when in transfer-instructions step
  useEffect(() => {
    if (step === "transfer-instructions" && autoPollStartTimeRef.current && !hasStartedPolling) {
      const elapsed = Date.now() - autoPollStartTimeRef.current;
      const remaining = 30000 - elapsed; // 30 seconds
      
      if (remaining > 0) {
        const timeoutId = setTimeout(() => {
          if (step === "transfer-instructions" && !hasStartedPolling) {
            handleStartPolling();
          }
        }, remaining);
        
        return () => clearTimeout(timeoutId);
      } else {
        // Already past 30 seconds, start immediately
        if (!hasStartedPolling) {
          handleStartPolling();
        }
      }
    }
  }, [step, hasStartedPolling, handleStartPolling]);

  const triggerNameEnquiry = async (bankCode, accountNumber) => {
    if (!bankCode || !accountNumber || accountNumber.length < 10) {
      return;
    }
    
    setIsVerifyingAccount(true);
    setAccountVerificationError(null);
    
    try {
      const result = await nameEnquiryMutation.mutateAsync({
        account_number: accountNumber,
        bank_code: bankCode
      });
      
      if (result.success && result.account_details?.verified) {
        const selectedBank = NIGERIAN_BANKS.find(b => b.code === bankCode);
        setFormData(prev => ({
          ...prev,
          account_name: result.account_details.account_name || "",
          bank_name: result.account_details.bank_name || selectedBank?.name || prev.bank_name
        }));
        setAccountVerificationError(null);
      } else {
        setAccountVerificationError(result.message || "Account verification failed");
        setFormData(prev => ({
          ...prev,
          account_name: ""
        }));
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          "Failed to verify account. Please check the account number and try again.";
      setAccountVerificationError(errorMessage);
      setFormData(prev => ({
        ...prev,
        account_name: ""
      }));
    } finally {
      setIsVerifyingAccount(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.account_number) {
      newErrors.account_number = "Account number is required";
    } else if (!/^\d{10}$/.test(formData.account_number)) {
      newErrors.account_number = "Account number must be 10 digits";
    }
    
    if (!formData.bank_code) {
      newErrors.bank_code = "Please select a bank";
    }
    
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone_number) {
      newErrors.phone_number = "Phone number is required";
    } else if (!/^[0-9]{10,11}$/.test(formData.phone_number)) {
      newErrors.phone_number = "Please enter a valid 10-11 digit phone number";
    }

    // Require account name verification
    if (!formData.account_name) {
      newErrors.account_number = "Please verify the account number. Account name is required.";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Countdown timer function
  const startCountdownTimer = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          clearInterval(countdownIntervalRef.current);
          if (!timeoutWarningShown) {
            setTimeoutWarningShown(true);
            toast({
              title: "Time Window Expired",
              description: "The 10-minute window has elapsed. If you've completed the transfer, it may take a few more moments to process.",
              status: "warning",
              duration: 8000,
              isClosable: true,
            });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [timeoutWarningShown, toast]);

  // Format time remaining (MM:SS)
  const formatTimeRemaining = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Normalize email before sending (trim and lowercase for consistency with Flutterwave)
      const normalizedFormData = {
        ...formData,
        email: formData.email.trim().toLowerCase(),
      };
      console.log("Submitting Direct Debit setup with data:", normalizedFormData);
      await setupDirectDebitPayload(normalizedFormData);
      // Response handling is done in the setupDirectDebit callback
    } catch (error) {
      console.error("Setup failed:", error);
      // Error handling is done in the setupDirectDebit error handler
    }
  };

  // Show error toast if setup fails
  useEffect(() => {
    if (setupDirectDebitError) {
      toast({
        title: "Setup Failed",
        description: setupDirectDebitError || "Failed to set up Direct Debit. Please check your details and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [setupDirectDebitError, toast]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <FiCreditCard />
            <Text>Setup Direct Debit</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={step === "polling"} />
        <ModalBody>
          {step === "form" && (
            <form onSubmit={handleSubmit}>
              <VStack spacing={6}>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>One-Time Authorization</AlertTitle>
                    <AlertDescription fontSize="sm">
                      You'll need to transfer ₦200 to authorize Direct Debit.
                      This is a one-time authorization that allows Weetrak to process
                      payroll payments directly from your bank account.
                    </AlertDescription>
                  </Box>
                </Alert>

                <FormControl isInvalid={!!errors.bank_code}>
                  <FormLabel>Bank</FormLabel>
                  <Select
                    placeholder="Select Bank"
                    value={formData.bank_code}
                    onChange={(e) => {
                      const selectedBank = NIGERIAN_BANKS.find(b => b.code === e.target.value);
                      setFormData({ 
                        ...formData, 
                        bank_code: e.target.value,
                        bank_name: selectedBank?.name || "",
                        account_name: "" // Clear account name when bank changes
                      });
                      setAccountVerificationError(null);
                      // Trigger name enquiry if account number is already filled
                      if (formData.account_number && formData.account_number.length >= 10) {
                        setTimeout(() => triggerNameEnquiry(e.target.value, formData.account_number), 100);
                      }
                    }}
                  >
                    {NIGERIAN_BANKS.map((bank) => (
                      <option key={bank.code} value={bank.code}>
                        {bank.name}
                      </option>
                    ))}
                  </Select>
                  {errors.bank_code && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.bank_code}
                    </Text>
                  )}
                </FormControl>

                <FormControl isInvalid={!!errors.account_number || !!accountVerificationError}>
                  <FormLabel>Account Number</FormLabel>
                  <Input
                    type="text"
                    placeholder="Enter 10-digit account number"
                    value={formData.account_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setFormData({
                        ...formData,
                        account_number: value,
                        account_name: "" // Clear account name when account number changes
                      });
                      setAccountVerificationError(null);
                    }}
                    maxLength={10}
                    isDisabled={isVerifyingAccount}
                  />
                  {isVerifyingAccount && (
                    <HStack mt={2} spacing={2}>
                      <Spinner size="sm" color="blue.500" />
                      <Text fontSize="xs" color="gray.600">
                        Verifying account...
                      </Text>
                    </HStack>
                  )}
                  {errors.account_number && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.account_number}
                    </Text>
                  )}
                  {accountVerificationError && !errors.account_number && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {accountVerificationError}
                    </Text>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Account Name</FormLabel>
                  <Input
                    value={formData.account_name}
                    readOnly
                    isDisabled
                    placeholder="Account name will be verified automatically"
                    bg="gray.50"
                    _disabled={{
                      opacity: 1,
                      cursor: "not-allowed"
                    }}
                  />
                  {formData.account_name && (
                    <HStack mt={2} spacing={2}>
                      <FiCheckCircle size={16} color="green" />
                      <Text fontSize="xs" color="green.600">
                        Account verified successfully
                      </Text>
                    </HStack>
                  )}
                </FormControl>

                <FormControl isInvalid={!!errors.email}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value.trim().toLowerCase() })
                    }
                  />
                  {errors.email && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.email}
                    </Text>
                  )}
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.phone_number}>
                  <FormLabel>Phone Number</FormLabel>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number (e.g., 08012345678)"
                    value={formData.phone_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setFormData({ ...formData, phone_number: value });
                    }}
                    maxLength={11}
                  />
                  {errors.phone_number && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.phone_number}
                    </Text>
                  )}
                </FormControl>

                <FormControl isInvalid={!!errors.address}>
                  <FormLabel>Address (Optional)</FormLabel>
                  <Input
                    type="text"
                    placeholder="Enter your address (e.g., Street, City, State) - Optional"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                  />
                  {errors.address && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.address}
                    </Text>
                  )}
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    If not provided, company address will be used
                  </Text>
                </FormControl>
              </VStack>
            </form>
          )}

          {step === "transfer-instructions" && mandateConsent && (
            <VStack spacing={6} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Complete Your Authorization</AlertTitle>
                  <AlertDescription fontSize="sm">
                    Transfer ₦200 to the account below to authorize Direct Debit. You have 10 minutes to complete this transfer.
                  </AlertDescription>
                </Box>
              </Alert>

              {/* Countdown Timer */}
              <Card bg="blue.50" border="1px solid" borderColor="blue.200">
                <CardBody>
                  <HStack justify="space-between" align="center">
                    <HStack spacing={2}>
                      <FiClock size={20} color="#3182ce" />
                      <Text fontWeight="600" color="blue.700">
                        Time Remaining:
                      </Text>
                    </HStack>
                    <Text fontSize="2xl" fontWeight="bold" color={timeRemaining < 60 ? "red.500" : "blue.700"}>
                      {formatTimeRemaining(timeRemaining)}
                    </Text>
                  </HStack>
                </CardBody>
              </Card>

              {/* Transfer Details Card */}
              <Card border="2px solid" borderColor="blue.200">
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Heading size="sm" color="gray.700">
                      Transfer Details
                    </Heading>
                    <Divider />
                    
                    <VStack spacing={3} align="stretch">
                      <Box>
                        <Text fontSize="xs" color="gray.600" mb={1}>
                          Bank Name
                        </Text>
                        <Text fontSize="lg" fontWeight="bold" color="gray.900">
                          {mandateConsent.bank_name}
                        </Text>
                      </Box>

                      <Box>
                        <Text fontSize="xs" color="gray.600" mb={1}>
                          Account Name
                        </Text>
                        <Text fontSize="lg" fontWeight="600" color="gray.900">
                          {mandateConsent.account_name}
                        </Text>
                      </Box>

                      <Box>
                        <HStack justify="space-between" align="center">
                          <Box flex={1}>
                            <Text fontSize="xs" color="gray.600" mb={1}>
                              Account Number
                            </Text>
                            <Text fontSize="xl" fontWeight="bold" color="blue.600" fontFamily="mono">
                              {mandateConsent.account_number}
                            </Text>
                          </Box>
                          <IconButton
                            aria-label="Copy account number"
                            icon={<FiCopy />}
                            onClick={onCopyAccountNumber}
                            colorScheme="blue"
                            variant="outline"
                            size="sm"
                          />
                        </HStack>
                        {accountNumberCopied && (
                          <Text fontSize="xs" color="green.600" mt={1}>
                            Account number copied!
                          </Text>
                        )}
                      </Box>

                      <Divider />

                      <Box>
                        <Text fontSize="xs" color="gray.600" mb={1}>
                          Amount to Transfer
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color="green.600">
                          {mandateConsent.amount || "₦200.00"}
                        </Text>
                      </Box>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Instructions */}
              {instructions && (
                <Card bg="gray.50">
                  <CardBody>
                    <Text fontSize="sm" color="gray.700" whiteSpace="pre-line">
                      {instructions}
                    </Text>
                  </CardBody>
                </Card>
              )}

              {/* Status indicator */}
              {hasStartedPolling && (
                <Alert status="info" borderRadius="md">
                  <Spinner size="sm" mr={3} />
                  <AlertDescription>
                    Checking for your transfer... This will continue automatically.
                  </AlertDescription>
                </Alert>
              )}
            </VStack>
          )}

          {step === "polling" && (
            <VStack spacing={6} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Verifying Authorization</AlertTitle>
                  <AlertDescription fontSize="sm">
                    We're checking for your transfer. This may take a few moments.
                  </AlertDescription>
                </Box>
              </Alert>

              <Card>
                <CardBody>
                  <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" thickness="4px" />
                    
                    {pollingStatus === "pending" && (
                      <VStack spacing={2}>
                        <Text fontSize="lg" fontWeight="600" color="gray.700">
                          Waiting for your transfer...
                        </Text>
                        <Text fontSize="sm" color="gray.600" textAlign="center">
                          Please complete the ₦200 transfer to the account provided.
                          We'll automatically detect when it's received.
                        </Text>
                      </VStack>
                    )}

                    {pollingStatus === "approved" && (
                      <VStack spacing={2}>
                        <HStack spacing={2}>
                          <FiCheckCircle size={24} color="orange" />
                          <Text fontSize="lg" fontWeight="600" color="orange.600">
                            Transfer received!
                          </Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600" textAlign="center">
                          Your authorization transfer has been received.
                          Activating your Direct Debit mandate...
                        </Text>
                      </VStack>
                    )}

                    {pollingStatus === "active" && (
                      <VStack spacing={2}>
                        <FiCheckCircle size={32} color="green" />
                        <Text fontSize="lg" fontWeight="600" color="green.600">
                          Direct Debit Authorized!
                        </Text>
                        <Text fontSize="sm" color="gray.600" textAlign="center">
                          Your Direct Debit mandate is now active and ready to use.
                        </Text>
                      </VStack>
                    )}

                    {timeoutWarningShown && (
                      <Alert status="warning" borderRadius="md" mt={4}>
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                          The 10-minute window has elapsed. If you've completed the transfer,
                          it may take a few more moments to process.
                        </AlertDescription>
                      </Alert>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {step === "form" && (
            <>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={setupDirectDebitIsLoading || isVerifyingAccount}
                isDisabled={isVerifyingAccount || !formData.account_name || !formData.phone_number}
              >
                {isVerifyingAccount ? "Verifying Account..." : "Continue"}
              </Button>
            </>
          )}

          {step === "transfer-instructions" && (
            <>
              <Button variant="ghost" mr={3} onClick={onClose} isDisabled={hasStartedPolling}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={handleStartPolling}
                isLoading={isPolling}
                isDisabled={hasStartedPolling || isPolling}
                leftIcon={<FiCheckCircle />}
              >
                {hasStartedPolling ? "Checking..." : "I've completed the transfer"}
              </Button>
            </>
          )}

          {step === "polling" && (
            <Button
              variant="ghost"
              onClick={onClose}
              isDisabled={pollingStatus === "active"}
            >
              {pollingStatus === "active" ? "Closing..." : "Close"}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DirectDebitSetup;

